import type { AccessArea, AccessCell, AccessLevel, AccessScope } from './areas';

// How access reads on screen. One place, so the matrix and a person's page
// say the same thing.

export const LEVEL_LABEL: Record<AccessLevel, string> = {
  none: 'No access',
  view: 'View',
  edit: 'Edit',
  manage: 'Manage',
};

export const SCOPE_LABEL: Record<AccessScope, string> = {
  area: 'own area',
  company: 'whole company',
};

/** A role's full name with its code, the way people say it. */
export const ROLE_NAME: Record<string, string> = {
  AO: 'Owner (AO)',
  CA: 'Company admin (CA)',
  BC: 'Base coordinator (BC)',
  Driver: 'Driver',
  Maintainer: 'Maintainer',
};

export const ROLE_LABEL: Record<string, string> = {
  AO: 'Owner',
  CA: 'Company admin',
  BC: 'BC',
  Driver: 'Driver',
  Maintainer: 'Maintainer',
};

// Grouped the way someone thinks about their company. Tasks is left out:
// the feature is not live.
export const AREA_GROUPS: { label: string; areas: { key: AccessArea; short: string; hint: string }[] }[] = [
  {
    label: 'Money',
    areas: [
      { key: 'payroll', short: 'Payroll', hint: 'Statements, weeks, driver pay' },
      { key: 'reports', short: 'Reports', hint: 'Payroll & revenue, weekly volume' },
      { key: 'fleetCost', short: 'Fleet cost', hint: 'What each truck costs' },
    ],
  },
  {
    label: 'Hiring & people',
    areas: [
      { key: 'hiring', short: 'Hiring', hint: 'The pipeline and checklist' },
      { key: 'hiringBackOffice', short: 'Back office', hint: 'Birth date, SSN, pay offered' },
      { key: 'hiringSetup', short: 'Setup', hint: 'Templates, interview guide, import' },
      { key: 'people', short: 'People', hint: 'Users: invite, edit, remove' },
    ],
  },
  {
    label: 'Operations',
    areas: [
      { key: 'files', short: 'Files', hint: 'Documents and compliance' },
      { key: 'restrictedFiles', short: 'Restricted', hint: 'I-9, W-4, statements' },
      { key: 'fleet', short: 'Fleet', hint: 'Trucks' },
      { key: 'accidents', short: 'Accidents', hint: 'Cases, claims, legal holds' },
      { key: 'operations', short: 'Operations', hint: 'Workflow runs' },
    ],
  },
  {
    label: 'Company',
    areas: [
      { key: 'companySettings', short: 'Settings', hint: 'Address, report time' },
      { key: 'audit', short: 'Audit log', hint: 'Who changed what' },
      { key: 'access', short: 'Access', hint: 'This page' },
    ],
  },
];

export const LEVEL_COLOR: Record<AccessLevel, string> = {
  none: 'text.disabled',
  view: 'info.dark',
  edit: 'warning.dark',
  manage: 'success.dark',
};

/**
 * "Edit · own area", or just "Manage". The whole company is the usual
 * case, so a cell only names its scope when it is narrower; the menu
 * spells out both.
 */
export function describeCell(cell: AccessCell | undefined, scoped: boolean): string {
  const level = cell?.level || 'none';
  if (!scoped || level === 'none' || cell?.scope !== 'area') return LEVEL_LABEL[level];
  return `${LEVEL_LABEL[level]} · ${SCOPE_LABEL.area}`;
}

/** Every choice a cell can take, as menu options. */
export function cellOptions(scoped: boolean): { value: string; cell: AccessCell; label: string }[] {
  const out: { value: string; cell: AccessCell; label: string }[] = [{ value: 'none', cell: { level: 'none' }, label: LEVEL_LABEL.none }];
  for (const level of ['view', 'edit', 'manage'] as AccessLevel[]) {
    if (scoped) {
      out.push({ value: `${level}:area`, cell: { level, scope: 'area' }, label: `${LEVEL_LABEL[level]} · ${SCOPE_LABEL.area}` });
      out.push({ value: `${level}:company`, cell: { level, scope: 'company' }, label: `${LEVEL_LABEL[level]} · ${SCOPE_LABEL.company}` });
    } else {
      out.push({ value: level, cell: { level }, label: LEVEL_LABEL[level] });
    }
  }
  return out;
}

export const cellValue = (cell: AccessCell | undefined, scoped: boolean) => {
  const level = cell?.level || 'none';
  if (level === 'none') return 'none';
  return scoped ? `${level}:${cell?.scope === 'area' ? 'area' : 'company'}` : level;
};

export const sameCell = (a: AccessCell | undefined, b: AccessCell | undefined, scoped: boolean) =>
  cellValue(a, scoped) === cellValue(b, scoped);
